import {
  ActionExecutionMessage,
  AgentStateMessage,
  Message,
  ResultMessage,
  Role,
  TextMessage,
} from '@copilotkit/runtime-client-gql';
import {
  CHATBOT_MESSAGES,
  CHATBOT_THREAD_ID_KEY,
  CHAT_PAGE_CONTEXT,
} from '@/constants/chatbot';
import { ROUTES } from '@shared/constants';
import { ACTION_HANDLER_STATUS } from '@/constants/project';
import {
  ArgsType,
  ProjectEvent,
  RawDataMessage,
  ChatPageContextType,
} from '@/types/chat';
import {
  getLocalStorage,
  isArray,
  removeLocalStorage,
  setLocalStorage,
} from '@shared/utils';
import {
  ActivityType,
  ProjectActivity,
  ProjectBase,
  ProjectStatus,
} from '@shared/types';
import { formalizeTrainingDate, isMatchedDateTime } from './date';

export const getResponsiveMessage = (
  messages: Message[],
  isPersist = false,
  isLoading = false,
) => {
  if (isLoading) {
    return null;
  }

  const lastThree = messages.slice(!isPersist ? -2 : -4);

  if (lastThree.length === (!isPersist ? 2 : 4)) {
    const textMessages = lastThree.filter(
      (msg) =>
        msg?.type === 'TextMessage' &&
        (msg as TextMessage)?.role === Role.Assistant,
    );
    const allUnresponsive =
      !!textMessages.length &&
      !!textMessages.every((msg) => !(msg as TextMessage)?.content);
    if (
      allUnresponsive &&
      !messages.find(
        (msg) => (msg as TextMessage)?.content === CHATBOT_MESSAGES.HANG_ON,
      )
    ) {
      const lastMessage = lastThree.at(-1)!;

      return new TextMessage({
        id: lastMessage.id,
        role: Role.Assistant,
        content: CHATBOT_MESSAGES.HANG_ON,
        createdAt: lastMessage.createdAt,
      });
    }
  }
  return null;
};

export const getResponsiveActionMessage = (
  messages: Message[],
  isLoading = false,
) => {
  if (isLoading) {
    return null;
  }

  const lastMessage = messages.slice(-1);
  let interruptedMessage: (TextMessage | ResultMessage)[] = [];
  if (
    (lastMessage as unknown as ActionExecutionMessage)?.type ===
    'ActionExecutionMessage'
  ) {
    interruptedMessage = [
      new ResultMessage({
        actionExecutionId: (lastMessage as unknown as ResultMessage).id,
        actionName: (lastMessage as unknown as ActionExecutionMessage).name,
        result: CHATBOT_MESSAGES.HANG_ON,
        createdAt: (lastMessage as unknown as ResultMessage).createdAt,
      }),
      new TextMessage({
        role: Role.Assistant,
        content: CHATBOT_MESSAGES.HANG_ON,
        createdAt: (lastMessage as unknown as ResultMessage).createdAt,
      }),
    ];
  }
  return interruptedMessage;
};

export const parsePersistentMessage = (storeMessages: Message[]) => {
  const messageLength = storeMessages.length;
  let interruptedMessage;
  const parsedMessages = storeMessages
    .map(
      (
        message: Message | TextMessage | ActionExecutionMessage | ResultMessage,
        index,
      ) => {
        if (message.type === 'TextMessage') {
          /**
           * Remove hanging out text message
           * This message will do nothing and stuck in queue that throws error
           */
          /**
           * Check if more than 3 last unresponsive messages
           */
          let responsiveMessage;
          if (index === messageLength - 1) {
            responsiveMessage = getResponsiveMessage(
              storeMessages,
              true,
              false,
            );
          }
          return (
            responsiveMessage ||
            new TextMessage({
              id: message.id,
              role: (message as TextMessage).role,
              content: (message as TextMessage).content,
              createdAt: message.createdAt,
              ...((message as unknown as RawDataMessage).image
                ? {
                    image: (message as unknown as RawDataMessage).image,
                  }
                : {}),
            })
          );
        } else if (message.type === 'ActionExecutionMessage') {
          /**
           * Inform hanging out action execution message
           * This message will do nothing and stuck in queue that throws error
           */
          if (index === messageLength - 1) {
            interruptedMessage = [
              new ResultMessage({
                actionExecutionId: (message as ResultMessage).id,
                actionName: (message as ActionExecutionMessage).name,
                result: CHATBOT_MESSAGES.INTERRUPTED,
                createdAt: message.createdAt,
              }),
              new TextMessage({
                role: Role.Assistant,
                content: CHATBOT_MESSAGES.INTERRUPTED,
                createdAt: message.createdAt,
              }),
            ];
          }

          return new ActionExecutionMessage({
            id: message.id,
            name: (message as ActionExecutionMessage).name,
            // ActionExecutionMessage doesn't support 'scope' in the constructor
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            scope: (message as any).scope,
            arguments: (message as ActionExecutionMessage).arguments,
            createdAt: message.createdAt,
            status: (message as ActionExecutionMessage).status,
          });
        } else if (message.type === 'ResultMessage') {
          return new ResultMessage({
            id: message.id,
            actionExecutionId: (message as ResultMessage).actionExecutionId,
            actionName: (message as ResultMessage).actionName,
            result: (message as ResultMessage).result,
            createdAt: message.createdAt,
          });
        } else if (message.type === 'AgentStateMessage') {
          return new AgentStateMessage({
            ...message,
          });
        } else {
          throw new Error(`Unknown message type: ${message.type}`);
        }
      },
    )
    .filter((item) => !!item);
  if (interruptedMessage && isArray(interruptedMessage)) {
    parsedMessages.push(
      ...(interruptedMessage as (
        | TextMessage
        | ActionExecutionMessage
        | ResultMessage
      )[]),
    );
  }
  return parsedMessages;
};

export const onCancelForm = (
  args: ArgsType,
  respond: (msg: string) => void = () => {},
) => {
  const cancelAction = CHATBOT_MESSAGES.CANCELLED;
  args.cancel = cancelAction;
  args.stacks = [];
  args.members = [];
  respond?.(cancelAction);
};

export const onResetActionArgs = (
  args: ArgsType,
  status: 'complete' | 'executing' | 'inProgress',
) => {
  const shouldResetState = status === ACTION_HANDLER_STATUS.INPROGRESS;

  if (shouldResetState) {
    args.cancel = '';
    args.error = '';
    args.succeed = false;
    args.stacks = [];
  }
};

export const onResetErrorAndCancelArgs = (args: ArgsType, result?: string) => {
  args.cancel = '';
  args.error = '';
  args.result = '';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  result = '';
};

export const isInterruptedAction = (result: string) => {
  return (
    !!result &&
    (result === CHATBOT_MESSAGES.HANG_ON ||
      result === CHATBOT_MESSAGES.INTERRUPTED)
  );
};

export const isCompletedAction = (args: ArgsType, result: string) => {
  return !args.cancel && !args.error && !isInterruptedAction(result);
};

type Role = 'user' | 'admin' | 'mentor' | 'supporter';

type ActionEntry = {
  action: string;
  role: Role[];
  updateEvents?: string[];
};

export const ACTION_MAP: Record<ProjectStatus | 'archived', ActionEntry[]> = {
  [ProjectStatus.WAITING_FOR_KICK_OFF]: [
    { action: ActivityType.PROJECT_UPDATE, role: ['admin'] },
    { action: ActivityType.PROJECT_KICK_OFF, role: ['admin'] },
    { action: ActivityType.PROJECT_ARCHIVE, role: ['admin'] },
    { action: ActivityType.ACTIVITY_UPDATE, role: ['admin'] },
    { action: ActivityType.PROJECT_SCHEDULE_ACTIVITY, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_JOIN, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_ROLE_CHANGE, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_LEAVE, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_LEAVE, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_RESUME, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_REMOVE, role: ['admin'] },
  ],
  [ProjectStatus.ACTIVE]: [
    { action: ActivityType.PROJECT_UPDATE, role: ['admin'] },
    { action: ActivityType.PROJECT_ARCHIVE, role: ['admin'] },
    { action: ActivityType.PROJECT_COMPLETE, role: ['admin'] },
    { action: ActivityType.PROJECT_GO_LIVE, role: ['admin'] },
    { action: ActivityType.PROJECT_RELEASE, role: ['admin'] },
    { action: ActivityType.PROJECT_PAUSE, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_JOIN, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_ROLE_CHANGE, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_LEAVE, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_LEAVE, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_RESUME, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_REMOVE, role: ['admin'] },
    { action: ActivityType.ACTIVITY_UPDATE, role: ['admin'] },
    { action: ActivityType.PROJECT_SCHEDULE_ACTIVITY, role: ['admin'] },
  ],
  [ProjectStatus.COMPLETED]: [
    { action: ActivityType.PROJECT_ARCHIVE, role: ['admin'] },
    { action: ActivityType.PROJECT_RESUME, role: ['admin'] },
    {
      action: ActivityType.ACTIVITY_UPDATE,
      role: ['admin'],
      updateEvents: [ActivityType.PROJECT_COMPLETE],
    },
    {
      action: ActivityType.PROJECT_SCHEDULE_ACTIVITY,
      role: ['admin'],
      updateEvents: [
        ActivityType.PROJECT_RESUME,
        ActivityType.PROJECT_PAUSE,
        ActivityType.PROJECT_COMPLETE,
      ],
    },
  ],
  [ProjectStatus.PAUSED]: [
    { action: ActivityType.PROJECT_RESUME, role: ['admin'] },
    { action: ActivityType.PROJECT_ARCHIVE, role: ['admin'] },
    { action: ActivityType.PROJECT_COMPLETE, role: ['admin'] },
    {
      action: ActivityType.ACTIVITY_UPDATE,
      role: ['admin'],
      updateEvents: [ActivityType.PROJECT_PAUSE],
    },
    {
      action: ActivityType.PROJECT_SCHEDULE_ACTIVITY,
      role: ['admin'],
      updateEvents: [
        ActivityType.PROJECT_PAUSE,
        ActivityType.PROJECT_RESUME,
        ActivityType.PROJECT_COMPLETE,
      ],
    },
  ],
  [ProjectStatus.RESUMED]: [
    { action: ActivityType.PROJECT_UPDATE, role: ['admin'] },
    { action: ActivityType.PROJECT_ARCHIVE, role: ['admin'] },
    { action: ActivityType.PROJECT_COMPLETE, role: ['admin'] },
    { action: ActivityType.PROJECT_GO_LIVE, role: ['admin'] },
    { action: ActivityType.PROJECT_RELEASE, role: ['admin'] },
    { action: ActivityType.PROJECT_PAUSE, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_JOIN, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_ROLE_CHANGE, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_LEAVE, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_LEAVE, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_RESUME, role: ['admin'] },
    { action: ActivityType.PROJECT_MEMBER_REMOVE, role: ['admin'] },
    { action: ActivityType.ACTIVITY_UPDATE, role: ['admin'] },
    { action: ActivityType.PROJECT_SCHEDULE_ACTIVITY, role: ['admin'] },
  ],
  archived: [
    { action: ActivityType.PROJECT_UNARCHIVE, role: ['admin'] },
    {
      action: ActivityType.ACTIVITY_UPDATE,
      role: ['admin'],
      updateEvents: [ActivityType.PROJECT_ARCHIVE],
    },
    {
      action: ActivityType.PROJECT_SCHEDULE_ACTIVITY,
      role: ['admin'],
      updateEvents: [
        ActivityType.PROJECT_ARCHIVE,
        ActivityType.PROJECT_UNARCHIVE,
      ],
    },
  ],
};

export const isActionAllowedPerRoleAndProjectStatus = (
  status: string,
  action: string,
  role: string,
  updateEvent = '',
): boolean => {
  if (!status || !action || !role) return false;

  // Normalize inputs
  const s = status.trim() as ProjectStatus;
  const a = action.trim();
  const r = role.trim();

  const entries = ACTION_MAP[s];
  if (!entries) return false;

  const entry = entries.find((e) => {
    if (e.updateEvents?.length && updateEvent) {
      return e.action === a && e.updateEvents.includes(updateEvent);
    }
    return e.action === a;
  });
  if (!entry) return false;
  return entry.role.includes(r as Role);
};

export const findActivity = (
  activities: ProjectActivity[],
  actionType: ActivityType,
  date: string,
  hasScheduledDateTime = false,
) => {
  return activities.find((act) => {
    if (
      act.type !== actionType &&
      act.type !== ActivityType.PROJECT_SCHEDULE_ACTIVITY
    ) {
      return false;
    }

    if (hasScheduledDateTime) {
      if (act.type !== ActivityType.PROJECT_SCHEDULE_ACTIVITY) return false;

      const actData = act.data as ProjectBase;

      return (
        date &&
        actData.scheduleType === actionType &&
        isMatchedDateTime(actData.scheduleTime || '', date)
      );
    }

    if (date && !isMatchedDateTime(act.occurredAt || '', date)) {
      return false;
    }

    return true;
  });
};

const getProjectEventActionType = (activity: ProjectActivity) => {
  if (activity.type === ActivityType.PROJECT_SCHEDULE_ACTIVITY) {
    return (activity.data as ProjectBase)?.scheduleType;
  }

  return activity.type;
};

const isProjectEventAction = (
  activity: ProjectActivity,
  actionType: ActivityType,
) => getProjectEventActionType(activity) === actionType;

const findActivityMatchingArgEvent = (
  activities: ProjectActivity[],
  actionType: ActivityType,
  date: string,
  hasScheduledDateTime: boolean,
) =>
  activities.find((activity) => {
    if (!date) return false;

    if (!isProjectEventAction(activity, actionType)) return false;

    if (hasScheduledDateTime) {
      if (activity.type !== ActivityType.PROJECT_SCHEDULE_ACTIVITY)
        return false;

      return isMatchedDateTime(
        (activity.data as ProjectBase)?.scheduleTime || '',
        date,
      );
    }

    if (activity.type === ActivityType.PROJECT_SCHEDULE_ACTIVITY) return false;

    return isMatchedDateTime(activity.occurredAt || '', date);
  });

const getProjectEventTimestamp = (activity: ProjectActivity) => {
  const timestamp = new Date(activity.occurredAt || '').getTime();

  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
};

const findLatestActivityByEventAction = (
  activities: ProjectActivity[],
  actionType: ActivityType,
) =>
  activities.reduce<ProjectActivity | undefined>((latestActivity, activity) => {
    if (!isProjectEventAction(activity, actionType)) return latestActivity;

    if (!latestActivity) return activity;

    return getProjectEventTimestamp(activity) >
      getProjectEventTimestamp(latestActivity)
      ? activity
      : latestActivity;
  }, undefined);

export const findExistingEvent = (
  argEvent: ProjectEvent,
  activities: ProjectActivity[],
  isUpdatingEvent: boolean,
) => {
  const hasScheduledDateTime = !!argEvent?.scheduledDateTime;
  const eventAction = argEvent?.name?.toLowerCase();
  const eventDateTime = argEvent?.scheduledDateTime || argEvent?.dateTime;

  const convertedDateTime = eventDateTime
    ? formalizeTrainingDate(eventDateTime)
    : '';
  const mappedAction = mapProjectAction(eventAction || '');
  const matchingDateTime =
    eventDateTime && eventDateTime.includes('T')
      ? convertedDateTime
      : eventDateTime || '';

  if (!isUpdatingEvent || !activities?.length || !mappedAction) {
    return {
      event: null,
      dateTime: convertedDateTime,
      isLatestFallback: false,
    };
  }

  const activityMatchByDateTime = findActivityMatchingArgEvent(
    activities,
    mappedAction as ActivityType,
    matchingDateTime,
    hasScheduledDateTime,
  );
  const event =
    activityMatchByDateTime ||
    findLatestActivityByEventAction(activities, mappedAction as ActivityType) ||
    null;

  return {
    event,
    dateTime: convertedDateTime,
    isLatestFallback: !activityMatchByDateTime && event !== null,
  };
};

/**
 * Checks whether a scheduled event has already happened.
 * It counts as "happened" if an official activity was recorded for it,
 * or if the scheduled time has already passed.
 */
export const checkHappenedOfficialEvent = (
  event: ProjectEvent | undefined,
  eventDateTime: string | undefined,
  activities: ProjectActivity[],
  isUpdating: boolean,
): boolean => {
  if (!eventDateTime) return false;

  const { event: scheduledEvent } = findExistingEvent(
    {
      name: event?.name,
      description: event?.description,
      scheduledDateTime: eventDateTime,
      dateTime: '',
    } as ProjectEvent,
    activities,
    isUpdating,
  );
  if (scheduledEvent?.type !== ActivityType.PROJECT_SCHEDULE_ACTIVITY)
    return false;

  const { event: officialEvent } = findExistingEvent(
    {
      name: event?.name,
      description: event?.description,
      scheduledDateTime: '',
      dateTime: eventDateTime,
    },
    activities,
    isUpdating,
  );

  if (
    officialEvent &&
    officialEvent.type !== ActivityType.PROJECT_SCHEDULE_ACTIVITY
  )
    return true;

  const scheduleTime = (scheduledEvent.data as ProjectBase)?.scheduleTime;

  return !!scheduleTime && new Date(scheduleTime) < new Date();
};

export const mapProjectAction = (action: string): string | null => {
  const mapping: Record<string, string> = {
    update: ActivityType.PROJECT_UPDATE,
    update_delete_stacks: ActivityType.PROJECT_UPDATE,
    invite_member: ActivityType.PROJECT_MEMBER_JOIN,
    leave_member: ActivityType.PROJECT_MEMBER_LEAVE,
    remove_member: ActivityType.PROJECT_MEMBER_REMOVE,
    resume_member: ActivityType.PROJECT_MEMBER_RESUME,
    change_role_member: ActivityType.PROJECT_MEMBER_ROLE_CHANGE,
    archive: ActivityType.PROJECT_ARCHIVE,
    unarchive: ActivityType.PROJECT_UNARCHIVE,
    release: ActivityType.PROJECT_RELEASE,
    go_live: ActivityType.PROJECT_GO_LIVE,
    kick_off: ActivityType.PROJECT_KICK_OFF,
    resume: ActivityType.PROJECT_RESUME,
    pause: ActivityType.PROJECT_PAUSE,
    complete: ActivityType.PROJECT_COMPLETE,
  };
  return mapping[action?.replaceAll('-', '_').toLowerCase()] || null;
};

const generateChatbotThreadId = (contextType: string): string =>
  `thread-${contextType}-chatbot-${Date.now()}`;

const getChatbotThreadIdKey = (contextType: string): string =>
  `${CHATBOT_THREAD_ID_KEY}-${contextType}`;

// Get existing chatbot thread ID from local storage or generate a new one if not exist
export const getChatbotThreadId = (contextType: string): string => {
  const key = getChatbotThreadIdKey(contextType);
  let threadId = getLocalStorage<string>(key);

  if (!threadId) {
    threadId = generateChatbotThreadId(contextType);
    setLocalStorage(key, threadId);
  }

  return threadId;
};

// Create new chatbot thread ID and save it to local storage
export const createNewChatbotThreadId = (contextType: string): string => {
  const key = getChatbotThreadIdKey(contextType);
  const threadId = generateChatbotThreadId(contextType);
  setLocalStorage(key, threadId);

  return threadId;
};

/**
 * Resolves the current chat page context from the active route.
 */
export const resolvePageContext = ({
  projectId,
  pathname,
}: {
  projectId?: string;
  pathname: string;
}): ChatPageContextType => {
  if (projectId) return CHAT_PAGE_CONTEXT.PROJECT;

  switch (true) {
    case pathname.startsWith(ROUTES.EMPLOYEE_TRAININGS):
      return CHAT_PAGE_CONTEXT.TRAINING;
    case pathname.startsWith(ROUTES.EMPLOYEES):
      return CHAT_PAGE_CONTEXT.EMPLOYEE;
    default:
      return CHAT_PAGE_CONTEXT.EMPLOYEE;
  }
};

/** Removes all chatbot thread ID keys from localStorage */
export const clearAllChatbotThreadIds = (): void => {
  if (typeof window === 'undefined') return;

  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(CHATBOT_THREAD_ID_KEY)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => removeLocalStorage(key));
};
